/**
 * The defining constants every conversion in this package is built from.
 *
 * Nothing here is measured or looked up: each base value below is *exact by definition*, fixed by
 * international agreement rather than by experiment. The international yard has been exactly
 * 0.9144 m since 1959, which makes the mile exactly 1609.344 m; the pound is exactly 0.45359237 kg
 * by the same agreement; the US gallon is exactly 231 cubic inches; the imperial gallon has been
 * exactly 4.54609 L since 1985.
 *
 * Every compound factor is then *computed* from those bases rather than pasted in. A reader can
 * see that `MPG_US_TO_L_PER_100KM` is one gallon spread over one mile, and cannot mistake it for a
 * number someone found somewhere. It also removes a whole class of defect: the codebase today
 * carries `0.621371`, `1.60934`, `3.79`, `6.89476` and `235.215`, each a differently-truncated
 * version of a value that has an exact form.
 */

/** Metres in an international yard — the 1959 agreement every length below descends from. */
const YARD_M = 0.9144;

const INCH_M = YARD_M / 36;

/** Standard gravity, the definition that turns a pound *mass* into a pound *force*. */
const STANDARD_GRAVITY_M_PER_S2 = 9.80665;

/** Kilometres in one international mile. Exact: 1760 yards. */
export const MILE_KM = (YARD_M * 1760) / 1000;

/** Litres in one US liquid gallon. Exact: 231 cubic inches. */
export const US_GALLON_L = 231 * INCH_M ** 3 * 1000;

/** Litres in one imperial gallon. Exact since the Weights and Measures Act 1985. */
export const UK_GALLON_L = 4.54609;

/** Kilograms in one avoirdupois pound. Exact since 1959. */
export const POUND_KG = 0.45359237;

const GRAMS_PER_KG = 1000;

/** Seconds in an hour, over metres in a kilometre: the m/s to km/h factor. */
export const MPS_TO_KMH = 3600 / 1000;

/** Miles in one kilometre. */
export const KM_TO_MILE = 1 / MILE_KM;

/**
 * Kilopascals in one psi.
 *
 * A pound-force is a pound mass under standard gravity, spread over a square inch. Deriving it
 * this way is why the value carries full precision instead of the `6.89476` in use today.
 */
export const PSI_KPA =
  (POUND_KG * STANDARD_GRAVITY_M_PER_S2) / INCH_M ** 2 / 1000;

/**
 * The reciprocal numerator taking US MPG to L/100km, and back.
 *
 * One gallon consumed over one mile is `US_GALLON_L` litres per `MILE_KM` kilometres; scaled to
 * 100 km it is this. Because the relationship inverts, the same constant serves both directions:
 * `lPer100km = MPG_US_TO_L_PER_100KM / mpg` and `mpg = MPG_US_TO_L_PER_100KM / lPer100km`.
 */
export const MPG_US_TO_L_PER_100KM = (US_GALLON_L * 100) / MILE_KM;

/** As above for the imperial gallon, which is roughly 20% larger. */
export const MPG_UK_TO_L_PER_100KM = (UK_GALLON_L * 100) / MILE_KM;

/** Reciprocal numerator taking miles per kWh to kWh per 100 km, and back. */
export const MI_PER_KWH_TO_KWH_PER_100KM = 100 / MILE_KM;

/** Grams per kilometre in one pound per mile. Linear: a mass spread over a distance. */
export const LB_PER_MI_TO_GR_PER_KM = (POUND_KG * GRAMS_PER_KG) / MILE_KM;

/**
 * Grams per kilometre in one gram per mile.
 *
 * The same shape as the pound-per-mile factor above — a mass spread over a mile, restated per
 * kilometre — with a numerator of one gram. Less than 1, because spreading the same mass over the
 * longer of the two distances gives the smaller rate: 100 g/mi is about 62 g/km.
 */
export const GR_PER_MI_TO_GR_PER_KM = 1 / MILE_KM;

/**
 * Fahrenheit to Celsius as a single affine step.
 *
 * `C = (F - 32) x 5/9` expands to `F x 5/9 - 32 x 5/9`, which is what the affine transform in the
 * registry consumes. Keeping the offset pre-multiplied means the transform applies one
 * multiplication and one addition, with no per-unit special case.
 */
export const FAHRENHEIT_TO_CELSIUS_FACTOR = 5 / 9;
export const FAHRENHEIT_TO_CELSIUS_OFFSET = -32 * FAHRENHEIT_TO_CELSIUS_FACTOR;
